/**
 * Screenshot helper for capturing canvas as PNG.
 *
 * Uses Excalidraw's export functions to capture the current canvas.
 */

import { exportToBlob } from "@excalidraw/excalidraw";

import type {
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import { calculateCanvasBounds } from "./canvasUtils";

const MIME_TYPES = {
  png: "image/png",
  jpg: "image/jpeg",
} as const;

/**
 * Capture canvas as PNG blob.
 *
 * @param excalidrawAPI - Excalidraw API instance
 * @returns PNG blob and dimensions
 */
export async function captureCanvas(
  excalidrawAPI: ExcalidrawImperativeAPI,
): Promise<{ blob: Blob; width: number; height: number }> {
  const elements = excalidrawAPI.getSceneElements();
  const appState = excalidrawAPI.getAppState();
  const files = excalidrawAPI.getFiles() as BinaryFiles;

  if (elements.length === 0) {
    throw new Error("No elements to capture");
  }

  // Export to blob with standard settings
  const blob = await exportToBlob({
    elements,
    appState: {
      ...appState,
      exportBackground: true,
      exportWithDarkMode: false,
    },
    files,
    mimeType: MIME_TYPES.png,
    exportPadding: 20,
  });

  // Get dimensions from canvas bounds
  const bounds = calculateCanvasBounds(elements);
  const width = Math.ceil(bounds.maxX - bounds.minX + 40); // +40 for padding
  const height = Math.ceil(bounds.maxY - bounds.minY + 40);

  return { blob, width, height };
}

/**
 * Convert blob to base64 data URL.
 *
 * @param blob - Image blob
 * @returns Base64 data URL
 */
export async function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert blob to data URL"));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
